<script lang="ts">
  import { onMount } from 'svelte'
  import { uploadAssistanceDoc } from '$lib/public/assistance-upload'
  import { apiBaseUrl } from '$lib/api/base-url'

  const params = new URLSearchParams(window.location.search)
  const confirmationId = params.get('id') ?? '-'
  const token = params.get('upload')
  const deferredLink = token
    ? `${window.location.origin}/thank-you?id=${confirmationId}&upload=${encodeURIComponent(token)}`
    : ''

  let status = $state<'idle' | 'uploading' | 'done' | 'error'>('idle')
  let errorMsg = $state('')
  let reqStatus = $state<string | null>(null)
  let needsInfoMessage = $state<string | null>(null)

  // Applicant can (re)upload only before review or when more info is requested.
  const canUpload = $derived(
    reqStatus === null || reqStatus === 'AWAITING_DOCS' || reqStatus === 'NEEDS_INFO',
  )

  async function refreshStatus(): Promise<void> {
    if (!token) return
    try {
      const res = await fetch(
        `${apiBaseUrl()}/public/assistance/status?token=${encodeURIComponent(token)}`,
      )
      if (!res.ok) return
      const data = (await res.json()) as { status: string; needsInfoMessage: string | null }
      reqStatus = data.status
      needsInfoMessage = data.needsInfoMessage
    } catch {
      /* status is best-effort; the upload control still works */
    }
  }

  async function onFile(e: Event): Promise<void> {
    const file = (e.currentTarget as HTMLInputElement).files?.[0]
    if (!file || !token) return
    status = 'uploading'
    try {
      await uploadAssistanceDoc(token, file)
      status = 'done'
      await refreshStatus()
    } catch {
      status = 'error'
      errorMsg = 'Upload failed - please try again or use the secure link below later.'
    }
  }

  onMount(refreshStatus)
</script>

<div class="card">
  <h1>Thanks - we've got your submission!</h1>
  <p>
    Your confirmation number is <strong>#{confirmationId}</strong>. A member of the property team
    will reach out shortly.
  </p>

  {#if token}
    <hr />
    <h2>Assistance animal documentation</h2>

    {#if reqStatus === 'APPROVED' || reqStatus === 'DENIED'}
      <p class="ok">Your request status: <strong>{reqStatus}</strong>.</p>
    {:else if reqStatus === 'DOCS_RECEIVED' || reqStatus === 'UNDER_REVIEW' || reqStatus === 'RECOMMENDED'}
      <p class="muted">Your documentation is with our review team. We'll be in touch.</p>
    {:else}
      {#if reqStatus === 'NEEDS_INFO' && needsInfoMessage}
        <p class="err">More information needed: {needsInfoMessage}</p>
      {:else}
        <p class="muted">
          Please provide a letter from a licensed health-care provider. We only ask what the law
          allows and never ask about your disability.
        </p>
      {/if}

      {#if status === 'done'}
        <p class="ok">Document received - thank you.</p>
      {:else if canUpload}
        <label class="upload">
          Upload {reqStatus === 'NEEDS_INFO' ? 'the additional document' : 'now'} (PDF, PNG, or JPEG,
          up to 10&nbsp;MB)
          <input type="file" accept="application/pdf,image/png,image/jpeg" onchange={onFile} />
        </label>
        {#if status === 'uploading'}<p class="muted">Uploading…</p>{/if}
        {#if status === 'error'}<p class="err">{errorMsg}</p>{/if}

        <p class="muted">Or upload later with this secure link:</p>
        <input class="link" type="text" readonly value={deferredLink} />
      {/if}
    {/if}
  {/if}

  <p class="muted">Please save your confirmation number for reference.</p>
</div>

<style>
  .card {
    background: var(--surface-2);
    padding: 32px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  h1 {
    margin: 0 0 12px;
    font-size: 1.5rem;
    color: var(--ink-strong);
  }
  h2 {
    margin: 16px 0 4px;
    font-size: 1.1rem;
    color: var(--ink-strong);
  }
  p {
    margin: 8px 0;
    color: var(--ink);
    line-height: 1.5;
  }
  .muted {
    color: var(--muted);
    font-size: 0.9rem;
  }
  .ok {
    color: var(--ok-ink);
    font-weight: 600;
  }
  .err {
    color: var(--err-ink);
  }
  hr {
    border: none;
    border-top: 1px solid var(--line);
    margin: 20px 0;
  }
  .upload {
    display: block;
    font-size: 0.9rem;
    color: var(--ink);
    margin-bottom: 12px;
  }
  .upload input {
    display: block;
    margin-top: 6px;
  }
  .link {
    width: 100%;
    box-sizing: border-box;
    padding: 8px;
    font-size: 0.8rem;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    color: var(--muted);
  }
</style>
